/* eslint-disable react-refresh/only-export-components -- shared util module, not a component file; it is .tsx only because badges carry icon JSX. */
/**
 * Single source of truth for handoff hotness badges.
 *
 * Every surface that renders a hotness badge — the dashboard handoff card, the
 * pending-handoff notification, the queue modal, and the SMS landing pages —
 * uses this helper, so adding or re-coloring a level is a one-file change.
 *
 * Two visual variants share the same level definitions:
 *   - 'solid' (default): opaque tinted pill (bg-*-100) used by the notification,
 *     queue modal, and SMS pages.
 *   - 'soft': translucent pill (bg-*-500/15) used by the dashboard card.
 */
import { Flame, AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';

export interface HotnessBadge {
  bg: string;
  text: string;
  border: string;
  icon: ReactNode | null;
  label: string;
}

export type HotnessVariant = 'solid' | 'soft';

type Tone = 'red' | 'orange' | 'yellow' | 'cyan' | 'blue' | 'gray';

// Level → display label, color tone, and icon. Backend vocabulary is
// urgent/hot/warm/cool/cold; 'high' is the score-system alias of the hot band.
interface LevelDef {
  label: string;
  tone: Tone;
  icon: ReactNode | null;
}

const LEVELS: Record<string, LevelDef> = {
  urgent: { label: 'Urgent', tone: 'red', icon: <Flame className="w-3 h-3" /> },
  hot: { label: 'Hot', tone: 'orange', icon: <Flame className="w-3 h-3" /> },
  high: { label: 'High', tone: 'orange', icon: <AlertTriangle className="w-3 h-3" /> },
  warm: { label: 'Warm', tone: 'yellow', icon: null },
  cool: { label: 'Cool', tone: 'cyan', icon: null },
  cold: { label: 'Cold', tone: 'blue', icon: null },
};

/** The hotness levels this app knows how to style. */
export const HOTNESS_LEVELS = Object.keys(LEVELS);

/** True when `level` is a recognized hotness level (drives the card's badge gate). */
export const isKnownHotness = (level: string | null | undefined): boolean =>
  !!level && level in LEVELS;

const SOLID: Record<Tone, Omit<HotnessBadge, 'icon' | 'label'>> = {
  red: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-700/30',
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-200 dark:border-orange-700/30',
  },
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-200 dark:border-yellow-700/30',
  },
  cyan: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-200 dark:border-cyan-700/30',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-700/30',
  },
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-200 dark:border-gray-700',
  },
};

const SOFT: Record<Tone, Omit<HotnessBadge, 'icon' | 'label'>> = {
  red: {
    bg: 'bg-red-500/15 dark:bg-red-500/25',
    text: 'text-red-700 dark:text-red-200',
    border: 'border-red-500/30 dark:border-red-500/40',
  },
  orange: {
    bg: 'bg-orange-500/15 dark:bg-orange-500/25',
    text: 'text-orange-700 dark:text-orange-200',
    border: 'border-orange-500/30 dark:border-orange-500/40',
  },
  yellow: {
    bg: 'bg-yellow-500/20 dark:bg-yellow-500/25',
    text: 'text-yellow-800 dark:text-yellow-200',
    border: 'border-yellow-500/30 dark:border-yellow-500/40',
  },
  cyan: {
    bg: 'bg-cyan-500/15 dark:bg-cyan-500/25',
    text: 'text-cyan-700 dark:text-cyan-200',
    border: 'border-cyan-500/30 dark:border-cyan-500/40',
  },
  blue: {
    bg: 'bg-blue-500/15 dark:bg-blue-500/25',
    text: 'text-blue-700 dark:text-blue-200',
    border: 'border-blue-500/30 dark:border-blue-500/40',
  },
  gray: {
    bg: 'bg-gray-500/15 dark:bg-gray-500/25',
    text: 'text-gray-700 dark:text-gray-200',
    border: 'border-gray-500/30 dark:border-gray-500/40',
  },
};

/** Badge style (bg/text/border/icon/label) for a hotness level. Unknown levels
 *  render as a neutral gray pill labeled with the raw value. */
export const getHotnessBadge = (
  level: string | null | undefined,
  variant: HotnessVariant = 'solid'
): HotnessBadge => {
  const def = LEVELS[level ?? ''];
  const palette = (variant === 'soft' ? SOFT : SOLID)[def?.tone ?? 'gray'];
  return {
    ...palette,
    icon: def?.icon ?? null,
    label: def?.label ?? level ?? '',
  };
};
